import { BreadcrumbItem, Breadcrumbs } from './Breadcrumbs'
import LocalizedLink from './Localization/LocalizedLink'
import { AddToPreferencesButton } from './_account/AddToPreferencesButton'
import { Box, Button, Container, OldHeading, Image, Link, OldText } from '@gassan-ui'
import { useAuth } from '@lib/hooks/use-auth'
import { getKontentImage } from '@lib/kontent'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'

type Props = {
  breadcrumbItems?: BreadcrumbItem[]
  title: string
  alt: string
  text: string
  onClickMakeAppointment?: () => void
  image?: string
  preferenceNumber?: number
  isPreOwned?: boolean
}

const OverviewHeader: FC<Props> = ({
  breadcrumbItems,
  title,
  alt,
  text,
  preferenceNumber,
  onClickMakeAppointment,
  image,
  isPreOwned = false,
}) => {
  const { isLoggedIn, isLoading } = useAuth()
  const showStorePreferenceButton = !isLoading && isLoggedIn && typeof preferenceNumber === 'number'
  const { t } = useTranslation(['navigation', 'other', 'global'])

  return (
    <Container
      position="relative"
      display="flex"
      alignItems="center"
      pt={{
        _: '2rem',
        medium: '2rem',
        large: '4rem',
      }}
      pb={{
        _: '2rem',
        medium: '2.5rem',
        large: '3rem',
      }}
    >
      <Box maxWidth="34rem">
        {breadcrumbItems && <Breadcrumbs items={breadcrumbItems} className="mb-12" />}
        <OldHeading
          mb={{
            _: '1rem',
            large: '1.5rem',
          }}
          variant="h1"
          as="h1"
        >
          {title}
        </OldHeading>
        <OldText
          mb={{
            _: '1.5rem',
            medium: '2rem',
            large: '2.5rem',
          }}
        >
          {text}
        </OldText>
        {isPreOwned ? (
          <>
            <Box mb="1rem">
              <LocalizedLink href={t('submitWatch', { ns: 'navigation' })}>
                <Link as="link" variant="brown">
                  {t('swapOrTradeWatch', { ns: 'other' })}
                </Link>
              </LocalizedLink>
            </Box>
            {onClickMakeAppointment && (
              <Box mb="1rem">
                <Link variant="blue" onClick={onClickMakeAppointment}>
                  {t('makeAppointmentPreOwned', { ns: 'global' })}
                </Link>
              </Box>
            )}
            <LocalizedLink href={`${t('faq-page-pre-owned', { ns: 'navigation' })}`}>
              <Link variant="blue">{t('faq', { ns: 'other' })}</Link>
            </LocalizedLink>
          </>
        ) : (
          onClickMakeAppointment && (
            <Button variant="outline" onClick={onClickMakeAppointment}>
              {t('makeAppointment', { ns: 'global' })}
            </Button>
          )
        )}
        {showStorePreferenceButton && (
          <Box mt="1rem">
            <AddToPreferencesButton variant="brands" preferenceNumber={preferenceNumber} />
          </Box>
        )}
      </Box>
      <Box
        display={{
          _: 'none',
          large: 'block',
        }}
        position="absolute"
        right="0"
        height="100%"
        width="50%"
        maxWidth="calc(100% - 34rem)"
        top="0"
      >
        {image && (
          <Image
            sources={[getKontentImage(image, { width: 900 })]}
            placeholderSrc={getKontentImage(image, { width: 20 })}
            className="h-full w-full"
            alt={alt}
          />
        )}
      </Box>
    </Container>
  )
}

export default OverviewHeader
